import { FC, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Bonus } from 'src/types/bonus';

import { BonusModal, BonusText, GiverName, parseContent } from '@bonusly/common-bonus';
import { Bookmark } from '@bonusly/common-bookmark';
import { RelativeTime } from '@bonusly/common-relative-time';
import { Box, Button, ClappingHandsSolid, createUseVariantStyle, Flex, HStack, Spacer, StyleProps, Text, VStack } from '@bonusly/design-system';

import { useBonus } from '../../hooks/use-bonus';
import { ReferenceUnlinkButton } from '../reference-unlink-button/reference-unlink-button';

export interface BonusCardV2Props extends StyleProps {
	// Backward compatible: support both object and ID
	bonus?: Bonus;
	bonusId?: string;
	onUnlinkBonus?: (bonusId: string) => void;
	sectionTitle?: string;
	variant?: 'flat' | 'default';
	showChildBonuses?: boolean;
	// Link to entity functionality (goals, topics, etc.)
	showLinkButton?: boolean;
	isLinked?: boolean;
	isLinkLoading?: boolean;
	onLink?: (bonusId: string) => void;
	onUnlink?: (bonusId: string) => void;
}

const useWrapperStyles = createUseVariantStyle({
	variant: {
		flat: {
			border: '1px solid',
			borderColor: 'border.default',
			boxShadow: 'none',
		},
		default: {
			border: 'none',
			boxShadow: 'sm',
		},
	},
});

export const BonusCardV2: FC<BonusCardV2Props> = ({
	bonus: bonusProp,
	bonusId,
	onUnlinkBonus,
	sectionTitle,
	showChildBonuses = false,
	showLinkButton = false,
	isLinked = false,
	isLinkLoading = false,
	onLink,
	onUnlink,
	variant = 'default',
	...rest
}) => {
	const { t } = useTranslation(['one_on_ones', 'common']);
	const [modalOpen, setModalOpen] = useState(false);

	// Fetch bonus by ID if only ID is provided
	const { data: fetchedBonus, isLoading: isBonusLoading } = useBonus(bonusId || '');

	// Use provided bonus object or fetched bonus
	const bonus = bonusProp || fetchedBonus;

	const wrapperStyles = useWrapperStyles({ variant });

	const { text } = useMemo(() => {
		return bonus ? parseContent(bonus) : { text: '' };
	}, [bonus]);

	// Show loading if we're fetching bonus by ID
	if (bonusId && isBonusLoading) {
		return (
			<Box bg="bg.surface" px={4} py={3} borderRadius="md" boxShadow="sm" mx="auto" sx={{ breakInside: 'avoid' }} {...rest}>
				<Text color="text.softest">Loading bonus...</Text>
			</Box>
		);
	}

	if (!bonus) return null;

	// Local decorator to ensure consistent small font size for all segments
	const decorateForSmallSize = (segment: any, type: string) => {
		if (type === 'MENTION' || type === 'USER_TAG') {
			// For user mentions and group mentions, wrap with small font size
			return (
				<Box
					as="span"
					fontSize="sm"
					data-component="bonus-text-segment"
					sx={{
						'& *': {
							fontSize: 'sm !important',
						},
					}}
				>
					{segment}
				</Box>
			);
		}
		if (type === 'AMOUNT') {
			return (
				<Box as="span" display="inline-block" px={1.5} borderRadius="md" bg="points.bgAlpha" color="points.fg" fontWeight="medium" fontSize="sm">
					+{bonus.amount}
				</Box>
			);
		}
		return segment;
	};

	const childBonuses = bonus.child_bonuses?.length || 0;
	const reactions = bonus.reactions?.length || 0;

	return (
		<Box
			bg="bg.surface"
			borderRadius="md"
			cursor="pointer"
			mx="auto"
			px={4}
			py={3}
			sx={{ breakInside: 'avoid' }}
			onClick={(e) => {
				// Don't open modal if clicking on interactive elements
				const target = e.target as HTMLElement;
				if (
					target.closest('button') ||
					target.closest('a') ||
					target.closest('[role=\"button\"]') ||
					target.closest('[role=\"menuitem\"]') ||
					target.closest('[role=\"option\"]') ||
					target.closest('video') ||
					target.closest('.video-controls')
				) {
					return;
				}
				setModalOpen(true);
			}}
			{...wrapperStyles}
			{...rest}
		>
			{modalOpen && (
				<BonusModal
					bonus={bonus}
					commentsDefaultOpen={false}
					handleCloseModal={() => {
						setModalOpen(false);
					}}
					isLoading={false}
				/>
			)}

			<Bookmark key={bonus.id} referenceId={bonus.id} referenceType="bonus" referenceText={bonus.reason} bookmark={bonus.bookmark}>
				<VStack alignItems="flex-start">
					<HStack alignItems="center" width="full">
						<Box h={8} w={8} borderRadius="full" bg="points.bgAlpha" display="flex" alignItems="center" justifyContent="center">
							<ClappingHandsSolid color="points.fgOnSurface" size={5} />
						</Box>

						<Text size="sm" fontWeight="semibold" color="text.default">
							{t('meeting.carousel.recognition')}
						</Text>

						<Spacer />

						<RelativeTime color="text.softest" date={new Date(bonus.created_at)} fontSize="sm" />

						{onUnlinkBonus && sectionTitle && <ReferenceUnlinkButton onClickUnlinkReference={() => onUnlinkBonus(bonus.id)} sectionTitle={sectionTitle} />}
					</HStack>

					<Box>
						<BonusText
							prefix={
								<Text as="span" fontWeight="semibold" size="sm" color="text.default">
									<GiverName bonus={bonus} />
								</Text>
							}
							bonus={bonus}
							fontSize="sm"
							decorateSegment={decorateForSmallSize}
							color="text.default"
							mt={-1}
							fontFamily="ui"
							text={text}
							noOfLines={7}
						/>
					</Box>

					{showChildBonuses && (
						<Flex dir="row" alignItems="center" pt={2}>
							{childBonuses > 0 && (
								<Text color="text.softer" size="sm">
									{t('meeting.bonus_footer.reply_count', {
										count: childBonuses,
									})}
								</Text>
							)}
							{childBonuses > 0 && reactions > 0 && <Box as="span" mx={1} borderRadius="full" w="2px" h="2px" bg="text.softer"></Box>}
							{reactions > 0 && (
								<Text color="text.softer" size="sm">
									{t('meeting.bonus_footer.reaction_count', {
										count: reactions,
									})}
								</Text>
							)}
							<Spacer />
							{onUnlinkBonus && sectionTitle && <ReferenceUnlinkButton onClickUnlinkReference={() => onUnlinkBonus(bonus.id)} sectionTitle={sectionTitle} />}
						</Flex>
					)}

					{showLinkButton && (
						<Flex justifyContent="center" pt={3} width="full">
							<Button
								size="sm"
								variant="alpha"
								colorScheme="primary"
								bg={isLinked ? 'error.bg' : 'primary.bgAlpha'}
								color={isLinked ? 'error.text' : 'primary.fg'}
								_hover={{
									bg: isLinked ? 'error.closeBg' : 'primary.bgAlphaHover',
									color: isLinked ? 'error.text' : 'primary.fg',
								}}
								onClick={(e) => {
									e.stopPropagation();
									if (isLinked) {
										onUnlink?.(bonus.id);
									} else {
										onLink?.(bonus.id);
									}
								}}
								width="full"
								isLoading={isLinkLoading}
								loadingText={isLinked ? 'Removing...' : 'Adding...'}
								disabled={isLinkLoading}
							>
								{isLinked ? 'Remove' : 'Add'}
							</Button>
						</Flex>
					)}
				</VStack>
			</Bookmark>
		</Box>
	);
};
